import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Newspaper, Calendar, ArrowRight, Search, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { resolveFileUrl } from '../../utils/fileUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const NewsDetailModal = ({ news, onClose }) => {
  if (!news) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="news-detail-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="news-detail-modal"
      >
        {/* Close Button */}
        <div className="sticky top-0 bg-white z-10 flex justify-end p-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="news-detail-close-btn"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Image */}
        {news.image && (
          <div className="w-full max-h-80 overflow-hidden">
            <img
              src={resolveFileUrl(news.image)}
              alt={news.title}
              className="w-full h-full object-cover"
              data-testid="news-detail-image"
            />
          </div>
        )}

        {/* Content */}
        <div className="p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            {news.category && (
              <span className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                {news.category}
              </span>
            )}
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <Calendar size={14} />
              {new Date(news.date).toLocaleDateString()}
            </span>
          </div>

          <h2 className="font-bold text-2xl md:text-3xl text-gray-900 mb-6" data-testid="news-detail-title">
            {news.title}
          </h2>

          <div className="prose prose-gray max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap" data-testid="news-detail-content">
            {news.content}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedNews, setSelectedNews] = useState(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data);
    } catch (error) {
      console.error('Failed to fetch news:', error);
    }
  };

  const filteredNews = news.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['all', ...new Set(news.map(item => item.category).filter(Boolean))];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="gradient-orange py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-6">
            <a href="/" className="hover:text-white">Home</a>
            <span>&rsaquo;</span>
            <span>News</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Latest News
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Stay updated with the latest news and announcements from our community
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Bar */}
      <div className="bg-white border-b border-gray-100 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                placeholder="Search news..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-gray-200 focus:border-primary"
              />
            </div>
            
            <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    selectedCategory === category
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category === 'all' ? 'All News' : category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {filteredNews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredNews.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-card hover:shadow-card-hover transition-all card-hover group cursor-pointer"
                onClick={() => setSelectedNews(item)}
                data-testid={`news-card-${index}`}
              >
                {item.image && (
                  <div className="h-48 bg-gray-100 overflow-hidden">
                    <img 
                      src={resolveFileUrl(item.image)} 
                      alt={item.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    {item.category && (
                      <span className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                        {item.category}
                      </span>
                    )}
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <Calendar size={14} />
                      {new Date(item.date).toLocaleDateString()}
                    </span>
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-3">{item.content}</p>
                  <span className="mt-4 text-primary font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read More <ArrowRight size={16} />
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Newspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No news found</h3>
            <p className="text-gray-500">
              {searchTerm ? 'Try adjusting your search terms' : 'Check back later for updates'}
            </p>
          </div>
        )}
      </div>

      {/* News Detail Modal */}
      <AnimatePresence>
        {selectedNews && (
          <NewsDetailModal news={selectedNews} onClose={() => setSelectedNews(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default NewsPage;
